import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Panel, Tag } from "capitalrx-components-2.0/lib/general";
import { Shelf, Stack } from "capitalrx-components-2.0/lib/layout";
import { Body, Subheading } from "capitalrx-components-2.0/lib/typography";

import { ScreenReader } from "@/App/components";
import { formatDollars } from "@/App/utils/utils";

import { AccumulatorProgressBar } from "@/App/components/AccumulatorProgressBar";
import {
  MoneyAccumulationValues,
  Accumulators,
  DisplayScopes,
} from "@/App/types/accumulation";
import { Decimal } from "decimal.js";
import styled from "styled-components";


import "./AccumulatorCard.scss";

const StyledPanel = styled(Panel)`
  background-color: #ffffff !important;
  border-color: #cfd9e1 !important;
  border-radius: 8px !important;
  border: 1px solid #cfd9e1 !important;
  height: 100%;
  padding: 24px !important;
`;

export interface AccumulatorCardProps {
  displayScope: DisplayScopes;
  name: Accumulators;
  accumulator: MoneyAccumulationValues;
  planHasAccumulator: boolean;
  tags: string[];
}

export const AccumulatorCard: FC<AccumulatorCardProps> = (props) => {

  const { t: translate } = useTranslation();

  const accumulatorLabels: Record<Accumulators, string> = {
    deductible: translate("ACCUMULATOR_CARD.deductible"),
    outOfPocket: translate("ACCUMULATOR_CARD.outOfPocket"),
    planPay: translate("ACCUMULATOR_CARD.planMaximum"),
    planPayFertility: translate("ACCUMULATOR_CARD.fertilityMaximum"),

  };

  const accumulatorLimitReachedLabels: Partial<Record<Accumulators, string>> = {
    planPay: translate("ACCUMULATOR_CARD.planLimitReached"),
  };

  const accumulatorName = props.name;
  const accumulator = props.accumulator ?? {
    max: new Decimal(0),
    spent: new Decimal(0),
    remaining: new Decimal(0),
    appliesTo: [],
  };

  function accumulatorTitle(accumulatorName: Accumulators): string {
    return `${accumulatorLabels[accumulatorName]}`;
  }

  const noAccumulatorDescription = useMemo(() => {
    if (accumulatorName === Accumulators.Deductible) {
      return translate("ACCUMULATOR_CARD.noAccumulatorDescriptionDeductible");
    } else if (accumulatorName === Accumulators.OutOfPocket) {
      return translate("ACCUMULATOR_CARD.noAccumulatorDescriptionOutOfPocket");
    } else if (accumulatorName === Accumulators.PlanPay) {
      return translate("ACCUMULATOR_CARD.noAccumulatorDescriptionPlanMaximum");
    }
  }, [accumulatorName]);

  const limitReached = useMemo(() => {
    return props.planHasAccumulator && accumulator.remaining.lessThanOrEqualTo(0);
  }, [accumulator.remaining]);

  return (
    <StyledPanel>
      <Stack spacing="medium">
        <Stack spacing="small">
          <Shelf spacing="mini" alignment="top">
            {props.tags?.includes("medical") && <Tag type="success" className="medicalTag">{translate("ACCUMULATOR_CARD.medical")}</Tag>}
            {props.tags?.includes("pharmacy") && <Tag type="system" className="pharmacyTag">{translate("ACCUMULATOR_CARD.pharmacy")}</Tag>}
            <Shelf.Spacer />
          </Shelf>
          <Subheading textAlign="left" className="accumulator-title">
            <ScreenReader>{translate("ACCUMULATOR_CARD.deductibleInformation")}</ScreenReader>
            <div data-testid={`div-test__${props.name}-title`}>
              {accumulatorTitle(props.name)}{" "}{}
            </div>
          </Subheading>
        </Stack>
        <Stack spacing="small" className="accumulator-progress-section">
          <Stack spacing="none">
            {limitReached && (
              <Body>{accumulatorLimitReachedLabels[accumulatorName] ?? translate("ACCUMULATOR_CARD.limitHasBeenReached")}</Body>
            )}
            {(!limitReached) && (
              <Body>{(props.planHasAccumulator && accumulator.max) ? translate("ACCUMULATOR_CARD.yourLimitIs") + " " + formatDollars(accumulator.max.toString()) : noAccumulatorDescription}</Body>
            )}
          </Stack>
          {props.planHasAccumulator && (
            <AccumulatorProgressBar
              accumulator={accumulator}
              spentHelpText={accumulatorName === "deductible" ? translate("ACCUMULATOR_CARD.covered") : translate("ACCUMULATOR_CARD.spent")}
              remainingHelpText={translate("ACCUMULATOR_CARD.remaining")}
              limitReached={limitReached}
              limitReachedWarning={limitReached}
              iconSize="small"
            />
          )}
        </Stack>
      </Stack>
    </StyledPanel>
  );
};
